<template>
  <div v-if="isGenerating" class="ds-info-box ds-info-box--info generation-banner">
    <v-progress-circular
      indeterminate
      size="20"
      width="2"
      color="blue"
      class="generation-banner__spinner" />
    <div class="ds-info-box__content">
      <div class="generation-banner__title">Generating release notes…</div>
      <div class="generation-banner__detail">
        AI is drafting the notes from the selected Jira tickets. This usually takes under a minute —
        the page updates itself as soon as the draft is ready.
      </div>
      <v-progress-linear indeterminate rounded height="3" color="blue" class="mt-3" />
    </div>
    <v-btn small text class="generation-banner__action" @click="$emit('refresh')">
      <v-icon left small>mdi-refresh</v-icon>
      Refresh
    </v-btn>
  </div>

  <div v-else-if="hasFailed" class="ds-info-box ds-info-box--error generation-banner">
    <v-icon class="ds-info-box__icon">mdi-alert-circle-outline</v-icon>
    <div class="ds-info-box__content">
      <div class="generation-banner__title">Generation failed</div>
      <div class="generation-banner__detail">{{ failureDetail }}</div>
    </div>
    <v-btn small outlined color="error" class="generation-banner__action" @click="onRegenerate">
      <v-icon left small>mdi-autorenew</v-icon>
      Regenerate
    </v-btn>
  </div>
</template>

<script>
  import { ReleaseStatus } from '@ReleaseNotes/config/enums';

  const GENERIC_FAILURE =
    'The AI could not produce a draft for this release. Try regenerating — if it keeps failing, check the Jira ticket selection on the Items tab.';

  export default {
    name: 'GenerationStatusBanner',
    props: {
      release: {
        type: Object,
        default: null,
      },
    },
    computed: {
      /**
       * The release status, tolerating both JSON casings the API may emit.
       * @returns {number|null}
       */
      status() {
        if (!this.release) return null;
        const value = this.release.status !== undefined ? this.release.status : this.release.Status;
        return value === undefined || value === null ? null : value;
      },
      isGenerating() {
        return this.status === ReleaseStatus.Generating;
      },
      hasFailed() {
        return this.status === ReleaseStatus.GenerationFailed;
      },
      /**
       * The server's failure reason. `lastError` casing is not guaranteed, so both spellings are
       * probed; a missing/blank reason degrades to generic guidance rather than an empty banner.
       * @returns {string}
       */
      failureDetail() {
        if (!this.release) return GENERIC_FAILURE;
        const raw =
          this.release.lastError !== undefined ? this.release.lastError : this.release.LastError;
        return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : GENERIC_FAILURE;
      },
    },
    methods: {
      onRegenerate() {
        this.$emit('regenerate');
      },
    },
  };
</script>

<style scoped lang="scss">
  .generation-banner {
    align-items: flex-start;

    &__spinner {
      flex-shrink: 0;
      margin-top: 2px;
    }

    &__title {
      font-weight: 500;
      color: var(--v-text-base);
    }

    &__detail {
      margin-top: 2px;
      color: var(--v-text_secondary-base);
    }

    &__action {
      flex-shrink: 0;
      align-self: center;
    }
  }
</style>
